/**
 * Question composer — value shape and the pure helpers that read it.
 *
 * Split out of `new-library-item-form.tsx` so that every host of the composer
 * body (the New question page, the import review pane) agrees on one schema
 * rather than each keeping its own idea of what a question is.
 */

import { z } from "zod"

import {
  AUTHORING_DEFAULT_OPTION_COUNT,
  AUTHORING_MIN_OPTION_COUNT,
  AUTHORING_MULTI_SELECT_SCORING_OPTIONS,
  AUTHORING_QUESTION_TYPES,
  AUTHORING_STATUS_OPTIONS,
  createAuthoringReference,
  type AuthoringMultiSelectScoring,
  type AuthoringQuestionType,
  type AuthoringReference,
} from "@/lib/library-authoring"
import type { LibraryFolder } from "@/lib/mock/library-folders"
import {
  getCategoryBreadcrumb,
  type ExamCategory,
} from "@/lib/exam-categories"
import {
  countFillBlankChipsInHtml,
  FILL_BLANK_MARKER,
  fillBlankLeadInPlainTextLength,
  normalizeFillBlankStem,
  removeFillBlankChipFromHtml,
} from "./fill-blank-html"
import { leadInPlainTextLength } from "@/lib/lead-in-html"

/**
 * Toggles whose label does not say what happens to the score keep their caption;
 * the rest ("Randomize options", "Case sensitive", …) explain themselves and the
 * captions only made the rail twice as tall.
 */
/** Inspector toggles rendered under Scoring (none at present — reserved). */
export const SCORING_TOGGLE_KEYS = new Set<string>()

const MULTI_SELECT_SCORING = AUTHORING_MULTI_SELECT_SCORING_OPTIONS.map(
  (o) => o.value,
) as [AuthoringMultiSelectScoring, ...AuthoringMultiSelectScoring[]]

const QUESTION_TYPES = AUTHORING_QUESTION_TYPES.map((t) => t.value) as [
  AuthoringQuestionType,
  ...AuthoringQuestionType[],
]

const STATUSES = AUTHORING_STATUS_OPTIONS.map((s) => s.value) as [
  (typeof AUTHORING_STATUS_OPTIONS)[number]["value"],
  ...(typeof AUTHORING_STATUS_OPTIONS)[number]["value"][],
]

const optionSchema = z.object({
  id: z.string(),
  text: z.string(),
  isCorrect: z.boolean(),
  rationale: z.string(),
})

const referenceSchema = z.object({
  id: z.string(),
  kind: z.enum(["citation", "link", "image", "document"]),
  citation: z.string(),
  url: z.string(),
  linkLabel: z.string(),
  fileName: z.string(),
  previewUrl: z.string(),
})

const matchingPairSchema = z.object({
  id: z.string(),
  left: z.string(),
  right: z.string(),
})

const orderedItemSchema = z.object({
  id: z.string(),
  text: z.string(),
})

const fillBlankAnswerSchema = z.object({
  id: z.string(),
  accepted: z.string(),
})

const hotspotRegionSchema = z.object({
  id: z.string(),
  shape: z.enum(["rect", "circle"]),
  label: z.string(),
  isCorrect: z.boolean(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
})

export const questionSchema = z
  .object({
    type: z.enum(QUESTION_TYPES),
    status: z.enum(STATUSES),
    folderIds: z.array(z.string()).min(1, "Pick at least one location."),
    /** Admin only: the faculty member who owns this question. Empty means me. */
    ownerEmail: z.string().optional(),
    categoryIds: z.array(z.string()),
    /** Who placed each tag, and how. See `lib/exam-categories/provenance`. */
    categoryProvenance: z
      .record(
        z.string(),
        z.object({
          origin: z.enum(["human", "ai-approved", "ai-edited"]),
          by: z.string(),
          at: z.string(),
          review: z.enum(["pending", "verified"]).optional(),
        }),
      )
      .optional(),
    /* The lead-in IS the question (rendered as the page heading). All
       question types share this one field; the schema requires a real
       sentence so reviewers can read it on its own. */
    /* Short name for the bank grid — not the prompt a student reads. */
    title: z.string().min(3, "Name this question so colleagues can find it."),
    leadIn: z.string(),
    options: z.array(optionSchema),
    rationale: z.string(),
    references: z.array(referenceSchema),
    /* Type-specific authoring fields — populated only by the matching builder. */
    numericValue: z.string(),
    numericTolerance: z.string(),
    numericUnits: z.string(),
    pairs: z.array(matchingPairSchema),
    orderedItems: z.array(orderedItemSchema),
    fillBlankAnswers: z.array(fillBlankAnswerSchema),
    hotspotImageName: z.string(),
    hotspotImageUrl: z.string(),
    hotspotRegions: z.array(hotspotRegionSchema),
    difficulty: z.enum(["easy", "medium", "hard", ""]),
    randomizeOptions: z.boolean(),
    caseSensitive: z.boolean(),
    negativeMarking: z.boolean(),
    multiSelectScoring: z.enum(MULTI_SELECT_SCORING),
    unlimitedSelections: z.boolean(),
    showFeedbackWhenReviewing: z.boolean(),
    eligibleForRandomDraw: z.boolean(),
    shuffleMatchingPairs: z.boolean(),
    shuffleOrderingItems: z.boolean(),
    points: z
      .string()
      .refine((v) => v.trim() === "" || /^\d+(\.\d+)?$/.test(v.trim()), {
        message: "Enter a positive number or leave blank.",
      }),
    bloom: z.string(),
    /** The admin's own classification types: type id to the level chosen. */
    classifications: z.record(z.string(), z.string()).optional(),
    cogLevel: z.string(),
    tags: z.array(z.string()),
  })
  .superRefine((data, ctx) => {
    const promptLen =
      data.type === "fill_blank"
        ? fillBlankLeadInPlainTextLength(data.leadIn)
        : leadInPlainTextLength(data.leadIn)
    if (promptLen < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["leadIn"],
        message: "Add the question prompt (at least a sentence).",
      })
    }

    const isMcq = data.type === "mcq_single" || data.type === "mcq_multiple"
    if (isMcq) {
      const filled = data.options.filter((o) => o.text.trim()).length
      if (filled < AUTHORING_MIN_OPTION_COUNT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["options"],
          message: `Provide at least ${AUTHORING_MIN_OPTION_COUNT} answer options.`,
        })
      }
      if (!data.options.some((o) => o.isCorrect)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["options"],
          message: "Mark at least one option as correct.",
        })
      }
    }
    if (data.type === "true_false" && !data.options.some((o) => o.isCorrect)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["options"],
        message: "Pick whether the statement is True or False.",
      })
    }
    if (data.type === "short_answer" && data.rationale.trim().length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rationale"],
        message: "Add the model answer and any acceptable variants.",
      })
    }
    if (data.type === "essay" && data.rationale.trim().length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rationale"],
        message: "Add a grading rubric so reviewers know how to score.",
      })
    }
    if (data.type === "numeric" && data.numericValue.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["numericValue"],
        message: "Enter the correct numeric value.",
      })
    }
    if (data.type === "matching") {
      const filled = data.pairs.filter(
        (p) => p.left.trim() && p.right.trim()
      ).length
      if (filled < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["pairs"],
          message: "Add at least two matching pairs.",
        })
      }
    }
    if (data.type === "ordering") {
      const filled = data.orderedItems.filter((i) => i.text.trim()).length
      if (filled < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["orderedItems"],
          message: "Add at least two items to order.",
        })
      }
    }
    if (data.type === "fill_blank") {
      const filled = data.fillBlankAnswers.filter((a) =>
        a.accepted.trim()
      ).length
      if (filled < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["fillBlankAnswers"],
          message: "Add at least one accepted answer.",
        })
      }
    }
    if (data.type === "hotspot") {
      if (!data.hotspotImageUrl.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["hotspotImageUrl"],
          message: "Upload a reference image.",
        })
      }
      if (data.hotspotRegions.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["hotspotRegions"],
          message: "Draw at least one region on the image.",
        })
      } else if (!data.hotspotRegions.some((r) => r.isCorrect)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["hotspotRegions"],
          message: "Mark at least one region as correct.",
        })
      }
    }
    data.references.forEach((ref, index) => {
      if (ref.kind === "citation" && isRichTextEmpty(ref.citation)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["references", index, "citation"],
          message: "Add a citation or remove the row.",
        })
      }
      if (ref.kind === "link" && ref.url.trim().length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["references", index, "url"],
          message: "Add a URL or remove the row.",
        })
      }
      if (ref.kind === "image" && ref.fileName.trim().length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["references", index, "fileName"],
          message: "Choose an image or remove the row.",
        })
      }
      if (ref.kind === "document" && ref.fileName.trim().length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["references", index, "fileName"],
          message: "Choose a document or remove the row.",
        })
      }
    })
  })

export type QuestionFormValues = z.infer<typeof questionSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

export const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"] as const

// Runtime ID generators — called from user actions (Add option, Add pair, etc.) which
// only fire AFTER hydration, so `Math.random()` is safe here. NEVER call these from
// `buildInitial*` factories below — those run during the server render too and a
// random ID would mismatch the client tree on hydration.
export function newOptionId() {
  return `opt-${Math.random().toString(36).slice(2, 9)}`
}
export function newReferenceId() {
  return `ref-${Math.random().toString(36).slice(2, 9)}`
}

const EMPTY_RICH_TEXT_MARKERS = new Set([
  "",
  "<p></p>",
  "<p><br></p>",
  "<p><br/></p>",
])

/** True when TipTap HTML is empty or whitespace-only. */
export function isRichTextEmpty(html: string): boolean {
  const trimmed = html.trim()
  if (EMPTY_RICH_TEXT_MARKERS.has(trimmed)) return true
  const plain = trimmed.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim()
  return plain.length === 0
}

/** True when any reference row carries author-visible content. */
export function referencesHasContent(refs: AuthoringReference[]): boolean {
  return refs.some((ref) => {
    if (ref.kind === "citation") {
      return !isRichTextEmpty(ref.citation)
    }
    if (ref.kind === "link") {
      return ref.url.trim().length > 0
    }
    if (ref.kind === "image") {
      return ref.fileName.trim().length > 0 || ref.previewUrl.length > 0
    }
    if (ref.kind === "document") {
      return ref.fileName.trim().length > 0
    }
    return false
  })
}

/** Read the single citation block stored in the references array. */
export function referencesRichTextHtml(refs: AuthoringReference[]): string {
  return refs.find((ref) => ref.kind === "citation")?.citation ?? ""
}

/** Extension label for an attached reference document. */
export function referenceDocumentLabel(name: string): string {
  const ext = name.split(".").pop()?.trim()
  if (!ext || ext === name) return "Document"
  return ext.toUpperCase()
}

/** Font Awesome class for an attached reference document. */
export function referenceDocumentIconClass(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase()
  if (ext === "pdf") return "fa-light fa-file-pdf"
  if (ext === "doc" || ext === "docx") return "fa-light fa-file-word"
  if (ext === "ppt" || ext === "pptx") return "fa-light fa-file-powerpoint"
  return "fa-light fa-file-lines"
}

/** Sync rich-text HTML to one citation row (drops empty citation rows). */
export function referencesFromRichTextHtml(
  html: string,
  existing: AuthoringReference[],
): AuthoringReference[] {
  const withoutCitation = existing.filter((ref) => ref.kind !== "citation")
  if (isRichTextEmpty(html)) return withoutCitation
  const prev = existing.find((ref) => ref.kind === "citation")
  const base =
    prev ?? createAuthoringReference(newReferenceId(), "citation")
  return [
    ...withoutCitation,
    {
      ...base,
      kind: "citation" as const,
      citation: html,
    },
  ]
}
export function newPairId() {
  return `pair-${Math.random().toString(36).slice(2, 9)}`
}
export function newOrderedId() {
  return `ord-${Math.random().toString(36).slice(2, 9)}`
}
export function newBlankId() {
  return `blk-${Math.random().toString(36).slice(2, 9)}`
}
export function newHotspotRegionId() {
  return `hs-${Math.random().toString(36).slice(2, 9)}`
}

// SSR-safe defaults — deterministic, index-based IDs so the server and client render
// the same `id` / `key` / DOM attributes (e.g. `Checkbox` IDs derived from `option.id`).
// Hydration mismatch issue: `Math.random()` produces different values per call, so
// using it in initial defaults makes every render emit a different tree.
export function buildInitialOptions(
  type: AuthoringQuestionType
): QuestionFormValues["options"] {
  if (type === "true_false") {
    return [
      { id: "opt-true", text: "True", isCorrect: false, rationale: "" },
      { id: "opt-false", text: "False", isCorrect: false, rationale: "" },
    ]
  }
  if (type === "mcq_single" || type === "mcq_multiple") {
    return Array.from({ length: AUTHORING_DEFAULT_OPTION_COUNT }, (_, i) => ({
      id: `opt-init-${i + 1}`,
      text: "",
      isCorrect: false,
      rationale: "",
    }))
  }
  return []
}

export function buildInitialPairs(): QuestionFormValues["pairs"] {
  return Array.from({ length: 3 }, (_, i) => ({
    id: `pair-init-${i + 1}`,
    left: "",
    right: "",
  }))
}
export function buildInitialOrderedItems(): QuestionFormValues["orderedItems"] {
  return Array.from({ length: 4 }, (_, i) => ({
    id: `ord-init-${i + 1}`,
    text: "",
  }))
}
export function buildInitialFillBlankAnswers(): QuestionFormValues["fillBlankAnswers"] {
  return [{ id: "blk-init-1", accepted: "" }]
}

export {
  FILL_BLANK_MARKER,
  FILL_BLANK_CHIP_CLASS,
  appendFillBlankChipToHtml,
  fillBlankLeadInToEditorHtml,
  fillBlankStemForDelivery,
  isFillBlankRichHtml,
  normalizeFillBlankStem,
} from "./fill-blank-html"

/** Count blank chips / `{{blank}}` markers in the lead-in. */
export function countFillBlankMarkers(leadIn: string): number {
  return countFillBlankChipsInHtml(leadIn)
}

/** Insert a blank marker at a caret offset in a plain-text stem. */
export function insertFillBlankMarkerInText(
  text: string,
  caret: number,
): { text: string; caret: number } {
  const normalized = normalizeFillBlankStem(text)
  const marker = FILL_BLANK_MARKER
  const before = normalized.slice(0, caret)
  const after = normalized.slice(caret)
  const needsSpaceBefore = before.length > 0 && !/\s$/.test(before)
  const needsSpaceAfter = after.length > 0 && !/^\s/.test(after)
  const insert = `${needsSpaceBefore ? " " : ""}${marker}${needsSpaceAfter ? " " : ""}`
  const nextText = before + insert + after
  return { text: nextText, caret: before.length + insert.length }
}

/** Remove the blank marker at `blankIndex` (0-based). */
export function removeFillBlankMarkerAtIndex(
  leadIn: string,
  blankIndex: number,
): string {
  return removeFillBlankChipFromHtml(leadIn, blankIndex)
}

/** Grow or shrink accepted-answer rows to match blank markers (min 1 row). */
export function syncFillBlankAnswersFromMarkers(
  leadIn: string,
  existing: QuestionFormValues["fillBlankAnswers"],
): QuestionFormValues["fillBlankAnswers"] {
  const targetCount = Math.max(1, countFillBlankMarkers(leadIn))
  const next = existing.slice(0, targetCount)
  while (next.length < targetCount) {
    next.push({ id: newBlankId(), accepted: "" })
  }
  return next
}

/** @deprecated Use `countFillBlankMarkers`. */
export function maxFillBlankMarkerIndex(leadIn: string): number {
  return countFillBlankMarkers(leadIn)
}

export function folderBreadcrumb(
  folderId: string,
  folders: LibraryFolder[]
): string {
  const f = folders.find((x) => x.id === folderId)
  if (!f) return ""
  if (f.parentId == null) return f.name
  const parent = folders.find((x) => x.id === f.parentId)
  return parent ? `${parent.name} / ${f.name}` : f.name
}

export function categoryBreadcrumb(
  categoryId: string,
  categories: ExamCategory[],
): string {
  return getCategoryBreadcrumb(categories, categoryId)
    .map((category) => category.name)
    .join(" / ")
}

/**
 * Folder-aware difficulty insight — derived deterministically from the
 * folder id so the same folder always returns the same numbers. In a real
 * build this comes from analytics; for the mock it is a stable seed so
 * the inspector reads as if the AI had crunched historical data.
 */
export function difficultyInsightForFolder(folder: LibraryFolder | undefined): {
  /** Predicted level based on the content the author is writing. */
  recommendation: "easy" | "medium" | "hard"
  /** Contextual note shown under the meter (e.g. folder distribution). */
  note: string
  /** Point-Biserial Index (0–1) — predicted question quality. */
  pbi: number
  /** Average folder difficulty (0–100, same scale as the meter). */
  averagePercent: number
} {
  if (!folder) {
    return {
      recommendation: "medium",
      note: "Pick a location to see how your question compares to this folder.",
      pbi: 0.3,
      averagePercent: 50,
    }
  }
  // Deterministic hash of folder id → stable mock numbers per folder.
  let h = 0
  for (let i = 0; i < folder.id.length; i++)
    h = (h * 31 + folder.id.charCodeAt(i)) >>> 0
  const tilt = (h % 100) / 100 // 0..1
  const mediumShare = 45 + Math.floor(tilt * 30) // 45..75 %
  const pbi = 0.22 + tilt * 0.22 // 0.22..0.44
  const averagePercent = 35 + Math.floor(tilt * 35) // 35..70
  const recommendation: "easy" | "medium" | "hard" =
    averagePercent < 40 ? "easy" : averagePercent > 65 ? "hard" : "medium"
  return {
    recommendation,
    note: `Based on your content, this question is predicted ${recommendation}. ${mediumShare}% of items in ${folder.name} are Medium (avg PBI ${pbi.toFixed(2)}).`,
    pbi,
    averagePercent,
  }
}
